import { useState } from 'react';

import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

const formatFt = (eft) =>
  `${Math.round(eft * 1000).toLocaleString('hu-HU')} Ft`;
const formatPct = (total, income) =>
  income > 0 ? `${((total / income) * 100).toFixed(2)}%` : '–';

// tétel sorai: minden oszlophoz { v, color, bold } vagy null
const buildRows = ({
  taxes,
  income,
  expense,
  koltseghanyad,
  ipaKulcs,
  cegesJovedelem,
}) => {
  const { kata, atalany, teteles, kft } = taxes;
  const red = (v) => ({ v, color: 'red' });
  const plain = (v) => ({ v });
  const bold = (v) => ({ v, bold: true });
  const all = (f, v) => ({ kata: f(v), atalany: f(v), teteles: f(v), kft: f(v) });

  return [
    { label: 'Bevétel', cells: all(plain, income) },
    { label: 'KATA', cells: { kata: red(kata.kata) } },
    { label: 'Plusz fizetendő KATA', cells: { kata: red(kata.overIncome) } },
    cegesJovedelem
      ? { label: 'Céges jövedelem (15%)', cells: { kata: red(kata.ceges) } }
      : null,
    {
      label: `IPA (${ipaKulcs.toLocaleString('hu-HU')}%)`,
      cells: {
        kata: red(kata.ipa),
        atalany: red(atalany.ipa),
        teteles: red(teteles.ipa),
        kft: red(kft.ipa),
      },
    },
    {
      label: `Költséghányad (${koltseghanyad * 100}%)`,
      cells: { atalany: plain(atalany.koltseghanyad) },
    },
    {
      label: 'Jövedelem',
      cells: {
        atalany: plain(atalany.jovedelem),
        teteles: bold(teteles.jovedelem),
        kft: bold(kft.jovedelem),
      },
    },
    { label: 'Mentesített adóalap', cells: { atalany: plain(atalany.mentesitett) } },
    { label: 'Korrigált adóalap', cells: { atalany: bold(atalany.adoalap) } },
    { label: 'TAO (9%)', cells: { kft: red(kft.tao) } },
    { label: 'Osztalék', cells: { kft: bold(kft.osztalek) } },
    { label: 'VSZJA (9%)', cells: { teteles: red(teteles.vszja) } },
    {
      label: 'SZJA (15%)',
      cells: {
        atalany: red(atalany.szja),
        teteles: red(teteles.szja),
        kft: red(kft.szja),
      },
    },
    {
      label: 'TB járulék (18,5%)',
      cells: { atalany: red(atalany.tb), teteles: red(teteles.tb) },
    },
    {
      label: 'SZOCHO (13%)',
      cells: {
        atalany: red(atalany.szocho),
        teteles: red(teteles.szocho),
        kft: red(kft.szocho),
      },
    },
    { label: 'Valós költség', cells: all(red, expense) },
  ]
    .filter(Boolean)
    // a bevételi korlát felett nem választható forma oszlopa üres marad
    .map((row) => ({
      ...row,
      cells: Object.fromEntries(
        Object.entries(row.cells).filter(([key]) => isAvailable(taxes, key))
      ),
    }))
    .filter((row) => Object.keys(row.cells).length > 0);
};

const isAvailable = (taxes, key) => taxes[key].available !== false;

const NotAvailable = () => (
  <Box component='span' sx={{ color: 'text.secondary' }}>
    Nem választható
  </Box>
);

const totalText = (total, income) =>
  `${formatFt(total)} (${formatPct(total, income)})`;

// mennyivel több adó a legolcsóbb választható formához képest
const diffsToCheapest = (schemes, taxes) => {
  const available = schemes.filter((s) => isAvailable(taxes, s.key));
  const min = Math.min(...available.map((s) => taxes[s.key].total));
  return Object.fromEntries(
    available.map((s) => [s.key, taxes[s.key].total - min])
  );
};

// a különbség kerekítve, hogy pár forint eltérés ne számítson
const DiffText = ({ diff }) =>
  diff === undefined ? (
    '–'
  ) : Math.round(diff * 1000) === 0 ? (
    <Box component='span' sx={{ color: 'success.main', fontWeight: 'bold' }}>
      Legolcsóbb
    </Box>
  ) : (
    `+${formatFt(diff)}`
  );

const SeriesKey = ({ color }) => (
  <Box
    component='span'
    sx={{
      display: 'inline-block',
      width: 12,
      height: 2,
      borderRadius: 1,
      backgroundColor: color,
      verticalAlign: 'middle',
      marginRight: 1,
    }}
  />
);

const Cell = ({ cell, ...props }) => (
  <TableCell
    align='right'
    sx={{
      color: cell?.color === 'red' ? 'error.main' : undefined,
      fontWeight: cell?.bold ? 'bold' : undefined,
    }}
    {...props}
  >
    {cell ? formatFt(cell.v) : null}
  </TableCell>
);

function DesktopTable({ rows, schemes, taxes, income, expense }) {
  const diffs = diffsToCheapest(schemes, taxes);
  return (
    <TableContainer sx={{ maxWidth: '100%' }}>
      <Table aria-label='Adózási formák összehasonlítása'>
        <TableHead>
          <TableRow>
            <TableCell width={80} align='right'>
              Tétel
            </TableCell>
            {schemes.map((s) => (
              <TableCell key={s.key} width={100} align='right'>
                <SeriesKey color={s.color} />
                {s.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label}>
              <TableCell align='right'>{row.label}</TableCell>
              {schemes.map((s) => (
                <Cell key={s.key} cell={row.cells[s.key]} />
              ))}
            </TableRow>
          ))}
          <TableRow>
            <TableCell align='right'>Összes adó</TableCell>
            {schemes.map((s) => (
              <TableCell key={s.key} align='right'>
                {isAvailable(taxes, s.key) ? (
                  totalText(taxes[s.key].total, income)
                ) : (
                  <NotAvailable />
                )}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell style={{ fontWeight: 'bold' }} align='right'>
              Profit
            </TableCell>
            {schemes.map((s) => (
              <TableCell key={s.key} style={{ fontWeight: 'bold' }} align='right'>
                {isAvailable(taxes, s.key)
                  ? formatFt(income - expense - taxes[s.key].total)
                  : '–'}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell align='right'>Különbség a legolcsóbbhoz</TableCell>
            {schemes.map((s) => (
              <TableCell key={s.key} align='right'>
                <DiffText diff={diffs[s.key]} />
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Mobilon: összesítő formánként, alatta a kiválasztott forma részletei
function MobileView({ rows, schemes, taxes, income, expense }) {
  const [selected, setSelected] = useState('atalany');
  const scheme = schemes.find((s) => s.key === selected);
  const diffs = diffsToCheapest(schemes, taxes);

  return (
    <Box>
      <Table size='small' aria-label='Adózási formák összesítése'>
        <TableHead>
          <TableRow>
            <TableCell>Forma</TableCell>
            <TableCell align='right'>Összes adó</TableCell>
            <TableCell align='right'>Profit</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {schemes.map((s) => (
            <TableRow
              key={s.key}
              hover
              selected={s.key === selected}
              onClick={() => setSelected(s.key)}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell>
                <SeriesKey color={s.color} />
                {s.short}
              </TableCell>
              <TableCell align='right'>
                {isAvailable(taxes, s.key) ? (
                  <>
                    {formatFt(taxes[s.key].total)}
                    <Typography
                      variant='caption'
                      component='div'
                      sx={{ color: 'text.secondary' }}
                    >
                      {formatPct(taxes[s.key].total, income)}
                    </Typography>
                  </>
                ) : (
                  <NotAvailable />
                )}
              </TableCell>
              <TableCell align='right'>
                <Box component='span' sx={{ fontWeight: 'bold' }}>
                  {isAvailable(taxes, s.key)
                    ? formatFt(income - expense - taxes[s.key].total)
                    : '–'}
                </Box>
                <Typography
                  variant='caption'
                  component='div'
                  sx={{ color: 'text.secondary' }}
                >
                  <DiffText diff={diffs[s.key]} />
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
        Koppints egy sorra a részletekért.
      </Typography>
      <Typography variant='subtitle1' sx={{ marginTop: 2, fontWeight: 600 }}>
        <SeriesKey color={scheme.color} />
        {scheme.label} részletei
      </Typography>
      <Table size='small' aria-label={`${scheme.label} részletei`}>
        <TableBody>
          {rows
            .filter((row) => row.cells[selected])
            .map((row) => (
              <TableRow key={row.label}>
                <TableCell>{row.label}</TableCell>
                <Cell cell={row.cells[selected]} />
              </TableRow>
            ))}
          <TableRow>
            <TableCell>Összes adó</TableCell>
            <TableCell align='right'>
              {isAvailable(taxes, selected) ? (
                totalText(taxes[selected].total, income)
              ) : (
                <NotAvailable />
              )}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={{ fontWeight: 'bold' }}>Profit</TableCell>
            <TableCell align='right' style={{ fontWeight: 'bold' }}>
              {isAvailable(taxes, selected)
                ? formatFt(income - expense - taxes[selected].total)
                : '–'}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Különbség a legolcsóbbhoz</TableCell>
            <TableCell align='right'>
              <DiffText diff={diffs[selected]} />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
}

function Results({ mobile, ...props }) {
  const rows = buildRows(props);
  return mobile ? (
    <MobileView rows={rows} {...props} />
  ) : (
    <DesktopTable rows={rows} {...props} />
  );
}

export default Results;
